import { z } from 'zod';
import { VoucherAllowanceType, VoucherType } from '@prisma/client';

export const enum VoucherAllowanceStatus {
  NoOptionSelected = 'NoOptionSelected',
  Donated = 'Donated',
  // This is for vouchers, which have been initially part of the survey, we have to assume the user has taken them
  Taken = 'Taken',
  AvailableToBeRedeemed = 'AvailableToBeRedeemed',
  Redeemed = 'Redeemed',
}

const voucherStatusNoOptionSelected = z
  .object({})
  .extend({ type: z.literal(VoucherAllowanceStatus.NoOptionSelected) });
const voucherStatusGifted = z
  .object({})
  .extend({ type: z.literal(VoucherAllowanceStatus.Donated) });
const voucherTaken = z
  .object({})
  .extend({ type: z.literal(VoucherAllowanceStatus.Taken) });
const voucherStatusAvailableToBeRedeemed = z
  .object({
    voucherAllowanceId: z.number(),
    voucherAllowanceType: z.enum(VoucherAllowanceType),
  })
  .extend({ type: z.literal(VoucherAllowanceStatus.AvailableToBeRedeemed) });
const voucherStatusRedeemed = z
  .object({
    vouchers: z.array(
      z.object({
        code: z.string(),
        type: z.enum(VoucherType),
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
      }),
    ),
  })
  .extend({ type: z.literal(VoucherAllowanceStatus.Redeemed) });

const voucherStatus = z.object({
  surveyLinkId: z.number(),
  surveyIdentifier: z.string(),
  questionnaireLink: z.url(),
  questionnaireCompleted: z.boolean(),
  recordedFilloutDate: z.coerce.date().nullable().optional(),
  voucherStatus: z.discriminatedUnion('type', [
    voucherStatusNoOptionSelected,
    voucherStatusGifted,
    voucherTaken,
    voucherStatusAvailableToBeRedeemed,
    voucherStatusRedeemed,
  ]),
});
export type VoucherStatus = z.infer<typeof voucherStatus>;
export const surveyLinkVoucherStatus = z.array(voucherStatus);
export type SurveyLinkVoucherStatus = z.infer<typeof surveyLinkVoucherStatus>;

export const bmsSampleVoucherStatusSchema = z
  .discriminatedUnion('type', [
    voucherStatusAvailableToBeRedeemed,
    voucherStatusRedeemed,
  ])
  .and(
    z.object({
      broughtAt: z.coerce.date().nullable().optional(),
    }),
  )
  .nullable();
export type BmsSampleVoucherStatus = z.infer<
  typeof bmsSampleVoucherStatusSchema
>;

export const bdm1VoucherStatusSchema = z
  .discriminatedUnion('type', [
    voucherStatusAvailableToBeRedeemed,
    voucherStatusRedeemed,
  ])
  .nullable();
export type Bdm1VoucherStatus = z.infer<typeof bdm1VoucherStatusSchema>;

// Galaxus portion only: the BDM2 cash remainder is staff-settled and never handed out as a
// code, so it appears neither in these vouchers nor in galaxusValue.
// galaxusValue is what the participant is owed in Galaxus vouchers; it varies per outcome,
// so the amount cannot be derived from a fixed denomination.
export const bdm2VoucherStatusSchema = z
  .discriminatedUnion('type', [
    voucherStatusAvailableToBeRedeemed,
    voucherStatusRedeemed,
  ])
  .and(z.object({ galaxusValue: z.number() }))
  .nullable();
export type Bdm2VoucherStatus = z.infer<typeof bdm2VoucherStatusSchema>;

export const voucherStateResDto = z.object({
  surveyLinkVoucherStatus: surveyLinkVoucherStatus,
  questionnaireAnsweredPoints: z.number(),
  bmsSampleVoucherStatus: bmsSampleVoucherStatusSchema,
});

export type SurveyLinkVoucherStateResDto = z.infer<typeof voucherStateResDto>;
